import {FC, useEffect, useMemo} from 'react'

import cn from 'classnames/bind'
import {useRouter} from 'next/router'

import IconClose from 'assets/icons/close.svg'
import BlogItem from 'components/blocks/BlogItem'
import DocumentToolsItem from 'components/blocks/DocumentToolsItem'
import {getDocumentToolsConfig} from 'components/blocks/DocumentToolsItem/DocumentToolsItem'
import DocumentToolsList from 'components/blocks/DocumentToolsList'
import {Col, Container, Row} from 'components/elements/grid'
import Pagination from 'components/elements/Pagination'
import {IPaginationData} from 'components/elements/Pagination/Pagination'
import IconSearch from 'public/icons/search.svg'
import BlogSearch from 'src/components/blocks/BlogSearch'
import Button from 'src/components/elements/Button'
import {BLOG_PAGE_SIZE} from 'src/configs/constants'
import {TPost} from 'src/types/blog'
import {klaviyoTrack, posthogTrack} from 'src/utils'
import {useToggleState} from 'src/utils/hooks/useToggleState'

import s from './TemplateBlog.module.scss'

const cx = cn.bind(s)

const getPaginationData = (currentPage: number, pageCount: number): IPaginationData => {
	const linksData: IPaginationData['linksData'] = new Array(pageCount).fill('').map((_, idx) => {
		const pageNumber = idx + 1
		return {
			url: `/blog/${pageNumber === 1 ? '' : pageNumber}`,
			isActive: pageNumber === currentPage,
			pageNumber,
		}
	})

	const prevUrl = currentPage > 1 ? `/blog/${currentPage === 2 ? '' : currentPage - 1}` : undefined

	return {
		linksData,
		prevUrl,
		nextUrl: currentPage < pageCount ? `/blog/${currentPage + 1}` : undefined,
	}
}
interface ITemplateBlogProps {
	className?: string
	posts: TPost[]
	/** Count of all posts */
	count: number
}

const TemplateBlog: FC<ITemplateBlogProps> = ({className, posts, count}) => {
	const router = useRouter()

	const [isSearchOpened, toggleIsSearchOpened] = useToggleState()
	const currentPage = +(router.query.page as string) || 1
	const pageCount = Math.ceil(count / BLOG_PAGE_SIZE)
	const paginationData = useMemo(
		() => getPaginationData(currentPage, pageCount),
		[currentPage, pageCount],
	)
	const documentToolsConfig = getDocumentToolsConfig()

	useEffect(() => {
		posthogTrack('[BLOG] Page opened')
		klaviyoTrack('Page opened', {page: 'blog', href: window?.location?.href})
	}, [])

	useEffect(() => {
		if (currentPage > pageCount) {
			router.push('/blog/1')
		}
	}, [currentPage, pageCount, router])

	return (
		<div className={cx('wrapper', className)}>
			<Container>
				<div className={cx('head-wrapper')}>
					<h2 className={cx('heading')}>Blog</h2>
					<Button
						size="small"
						variant="secondary"
						className={cx('btn-search')}
						type="button"
						onClick={toggleIsSearchOpened}>
						{isSearchOpened ? (
							<IconClose className={cx('btn-search-close')} />
						) : (
							<IconSearch className={cx('btn-search-icon')} />
						)}
					</Button>

					<BlogSearch className={cx('search', {opened: isSearchOpened})} />
				</div>
				<p className={cx('sub-heading')}>
					Explore our blog to discover everything you need to know about paystubs, W-2 forms, tax
					forms, and much more.
				</p>
				<Row>
					<Col xl={8} className={cx('list-container')}>
						<ul className={cx('list')} style={{padding: 0}}>
							{posts.map((post, index) => (
								<li key={post._id} className={cx('list__li')}>
									<BlogItem className={cx('list__item')} post={post} index={index} />
								</li>
							))}
						</ul>
						<Pagination data={paginationData} className={cx('pagination')} />
					</Col>
					<Col as="ul" className={cx('d-none', 'd-xl-block', 'tools-list')} xl={{span: 4}}>
						{Object.keys(documentToolsConfig).map((key) => (
							<li key={key} className={cx('tools-list__item-wrapper')}>
								<DocumentToolsItem
									className={cx('tools-list__item')}
									variant={key as keyof typeof documentToolsConfig}
								/>
							</li>
						))}
					</Col>
				</Row>
			</Container>
			<DocumentToolsList className={cx('d-xl-none')} />
		</div>
	)
}

TemplateBlog.displayName = 'TemplateBlog'

export default TemplateBlog
